// src/@shared/utils/verifyInfinityOrbsClient.js
import { readContract } from "@wagmi/core";
import {
  doc,
  updateDoc,
  serverTimestamp,
  collection,
  writeBatch,
  getDocs,
  getDoc,
} from "firebase/firestore";
import { createPublicClient, http, parseAbi, parseAbiItem } from "viem";
import { abstract } from "viem/chains";
import { db } from "../firebase/config";
import { buildOrbLayersFromAttributes, normalizeOrbLayers } from "./orbAvatar";

const ERC721_ABI = parseAbi([
  "function balanceOf(address owner) view returns (uint256)",
  "function ownerOf(uint256 tokenId) view returns (address)",
  "function tokenURI(uint256 tokenId) view returns (string)",
]);

const TRANSFER_EVENT = parseAbiItem(
  "event Transfer(address indexed from, address indexed to, uint256 indexed tokenId)",
);

const ORB_CONTRACT = "0x9292f3982dec562980a8b6bd38d649a6f3cdab68";
const ABSTRACT_RPC_URL = "https://api.mainnet.abs.xyz";
const IPFS_GATEWAYS = [
  "https://ipfs.io/ipfs/",
  "https://gateway.pinata.cloud/ipfs/",
  "https://cloudflare-ipfs.com/ipfs/",
];

const publicClient = createPublicClient({
  chain: abstract,
  transport: http(ABSTRACT_RPC_URL),
});

const normalizeAddr = (value) => String(value || "").toLowerCase();

const toIpfsHttpUrls = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return [];
  if (raw.startsWith("ipfs://")) {
    const path = raw.replace("ipfs://", "").replace(/^ipfs\//, "");
    return IPFS_GATEWAYS.map((gateway) => `${gateway}${path}`);
  }
  return [raw];
};

const fetchJsonWithFallbacks = async (urls = []) => {
  let lastError = null;

  for (const url of urls) {
    try {
      const res = await fetch(url);
      if (!res.ok) {
        lastError = new Error(`Metadata request failed: ${res.status} ${res.statusText}`);
        continue;
      }
      return await res.json();
    } catch (error) {
      lastError = error;
    }
  }

  if (lastError) throw lastError;
  throw new Error("No metadata URL available for this orb.");
};

const normalizeAttributes = (attributes = []) =>
  (Array.isArray(attributes) ? attributes : [])
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const traitType =
        item?.trait_type || item?.traitType || item?.type || item?.name || "";
      const value =
        item?.value ??
        item?.display_value ??
        item?.displayValue ??
        item?.name ??
        "";

      if (!traitType && value === "") return null;

      return {
        traitType: String(traitType || ""),
        value: typeof value === "string" ? value : String(value),
      };
    })
    .filter(Boolean);

const normalizeImageUrl = (value) => toIpfsHttpUrls(value)[0] || "";

const buildFallbackLayersFromAttributes = (attributes = []) =>
  normalizeAttributes(attributes).map((attribute, index) => ({
    id: `${attribute.traitType || "layer"}-${index}`,
    name: `${attribute.traitType}: ${attribute.value}`,
    category: attribute.traitType,
    value: attribute.value,
    url: "",
    order: index,
    raw: attribute,
  }));

const toTokenIdArg = (tokenId) => {
  const value = Number(tokenId);
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new Error(`Unsupported token ID: ${tokenId}`);
  }
  return value;
};

const getOwnedTokenIds = async (wallet) => {
  const normalizedWallet = normalizeAddr(wallet);
  const [receivedLogs, sentLogs] = await Promise.all([
    publicClient.getLogs({
      address: ORB_CONTRACT,
      event: TRANSFER_EVENT,
      args: { to: wallet },
      fromBlock: 0n,
      toBlock: "latest",
    }),
    publicClient.getLogs({
      address: ORB_CONTRACT,
      event: TRANSFER_EVENT,
      args: { from: wallet },
      fromBlock: 0n,
      toBlock: "latest",
    }),
  ]);

  const candidateIds = new Set(
    receivedLogs.map((log) => String(log?.args?.tokenId ?? "")).filter(Boolean),
  );

  sentLogs.forEach((log) => {
    const tokenId = String(log?.args?.tokenId ?? "");
    if (tokenId) candidateIds.delete(tokenId);
  });

  const ownedTokenIds = [];
  for (const tokenId of candidateIds) {
    try {
      const owner = await publicClient.readContract({
        address: ORB_CONTRACT,
        abi: ERC721_ABI,
        functionName: "ownerOf",
        args: [toTokenIdArg(tokenId)],
      });
      if (normalizeAddr(owner) === normalizedWallet) {
        ownedTokenIds.push(tokenId);
      }
    } catch (error) {
      console.warn(`Failed to confirm owner for token ${tokenId}:`, error);
    }
  }

  return ownedTokenIds.sort((a, b) => Number(a) - Number(b));
};

const loadOrbAsset = async (tokenId) => {
  const tokenURI = await publicClient.readContract({
    address: ORB_CONTRACT,
    abi: ERC721_ABI,
    functionName: "tokenURI",
    args: [toTokenIdArg(tokenId)],
  });

  const metadata = await fetchJsonWithFallbacks(toIpfsHttpUrls(tokenURI));
  const attributes = normalizeAttributes(metadata?.attributes);
  const imageUrl = normalizeImageUrl(metadata?.image);
  const metadataLayers = normalizeOrbLayers(metadata?.layers || []);
  const mappedLayers = await buildOrbLayersFromAttributes(attributes);
  const layers = mappedLayers.length ? mappedLayers : metadataLayers;

  return {
    tokenId: String(tokenId),
    name: metadata?.name || `Infinity Orb #${tokenId}`,
    imageUrl,
    tokenUri: tokenURI,
    metadata,
    attributes,
    layers: layers.length ? layers : buildFallbackLayersFromAttributes(attributes),
    layerSource: mappedLayers.length
      ? "storage"
      : metadataLayers.length
        ? "metadata"
        : "attributes",
    standard: "ERC721",
    updatedAt: Date.now(),
  };
};

export async function verifyInfinityOrbsClient({ wagmiConfig, agwAddress, userId }) {
  if (!agwAddress) throw new Error("Missing AGW address");
  if (!userId) throw new Error("Missing userId");

  // 1) On-chain verify count
  const bal = await readContract(wagmiConfig, {
    address: ORB_CONTRACT,
    abi: ERC721_ABI,
    functionName: "balanceOf",
    args: [agwAddress],
  });

  const orbCount = Number(bal);

  // 2) Discover the actual owned token IDs directly from chain activity.
  const ownedTokenIds = await getOwnedTokenIds(agwAddress);

  if (ownedTokenIds.length !== orbCount) {
    console.warn(
      `Infinity Orb count mismatch: balanceOf=${orbCount}, discovered=${ownedTokenIds.length}`,
    );
  }

  const orbAssets = await Promise.all(ownedTokenIds.map((tokenId) => loadOrbAsset(tokenId)));

  // 3) Save basic verification to user doc
  const userRef = doc(db, "users", userId);
  const userSnap = await getDoc(userRef);
  const selectedOrbTokenId = userSnap.data()?.orbAvatar?.tokenId
    ? String(userSnap.data()?.orbAvatar?.tokenId)
    : "";
  const ownedTokenIdSet = new Set(orbAssets.map((asset) => String(asset?.tokenId || "")));
  const updateData = {
    orbCount,
    orbVerifiedAt: serverTimestamp(),
    orbWallet: agwAddress,
    orbContract: ORB_CONTRACT,
    orbImageCount: orbAssets.length,
  };
  if (selectedOrbTokenId && !ownedTokenIdSet.has(selectedOrbTokenId)) {
    updateData.orbAvatar = null;
  }
  await updateDoc(userRef, updateData);

  // 4) Save normalized orb data to the user subcollection and clear stale docs.
  const batch = writeBatch(db);
  const baseRef = collection(db, "users", userId, "InfinityOrbs");
  const existingDocs = await getDocs(baseRef);

  orbAssets.forEach((asset) => {
    if (!asset.tokenId) return;
    const orbRef = doc(baseRef, asset.tokenId);
    batch.set(
      orbRef,
      {
        ...asset,
        orbContract: ORB_CONTRACT,
        orbWallet: agwAddress,
        verifiedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  });

  existingDocs.forEach((snapshot) => {
    if (ownedTokenIdSet.has(snapshot.id)) return;
    batch.delete(snapshot.ref);
  });

  await batch.commit();

  return {
    orbCount,
    orbImages: orbAssets.map((asset) => asset.imageUrl).filter(Boolean),
    orbAssets,
    source: "onchain",
  };
}
